<template>
  <div
    class="slack-ua-setup-nudge-cta"
    :class="{ 'slack-ua-setup-nudge-cta--disabled': disabled }"
  >
    <span
      v-tooltip="disabled ? { placement: 'left' } : false"
      class="slack-ua-setup-nudge-cta__hint-wrap"
      :title="disabledHint"
    >
      <AppButton
        :type="buttonType"
        :size="buttonSize"
        :disabled="disabled || loading"
        data-qa="slack-ua-setup-nudge-cta"
        @click="handleClick"
      >
        {{ label }}
      </AppButton>
    </span>
  </div>
</template>

<script>
import { mapGetters } from 'vuex';
import AppButton from '~/components/AppButton.vue';
import { SETTINGS_SMART_NOTIFICATIONS } from '~/app_constants';
import { SmartNotificationsSettingsResource } from '~/resources';

const SLACK_UA_PRESET_KEY = 'unusual_activity_to_slack';

function slackUaNotificationParams() {
  const preset = SETTINGS_SMART_NOTIFICATIONS.presets[SLACK_UA_PRESET_KEY];
  const deliverBy = Object.entries(preset.deliver_by)
    .filter(([, enabled]) => enabled)
    .map(([channel]) => channel);

  return {
    name: preset.name,
    metric_type: preset.metric_type,
    frequency: preset.frequency,
    deliver_by: deliverBy,
    target: preset.target_all_members ? { members: ['all'] } : { members: [] },
    recipients: preset.recipients,
    source: preset.source,
  };
}

export default {
  name: 'SlackUaSetupNudgeCta',
  components: { AppButton },
  props: {
    source: {
      type: String,
      required: true,
    },
    ctaKind: {
      type: String,
      required: true,
    },
    connectSlackUrl: {
      type: String,
      default: '',
    },
    appearance: {
      type: String,
      default: 'primary',
    },
  },
  emits: ['completed'],
  data: () => ({
    loading: false,
  }),
  computed: {
    ...mapGetters(['organizationId']),
    disabled() {
      return this.ctaKind === 'disabled';
    },
    buttonType() {
      return this.appearance === 'outline' ? 'default' : 'primary';
    },
    buttonSize() {
      return this.appearance === 'outline' ? 'sm' : undefined;
    },
    label() {
      return this.ctaKind === 'enable' ? 'Enable Slack notifications' : 'Connect Slack';
    },
    disabledHint() {
      return this.disabled
        ? "You're at your maximum number of integrations, upgrade to Team to connect Slack"
        : null;
    },
  },
  methods: {
    connectSlackUrlWithSurface() {
      const url = new URL(this.connectSlackUrl, window.location.origin);

      url.searchParams.set('nudge_surface', this.source);

      return `${url.pathname}${url.search}`;
    },
    handleClick() {
      if (this.disabled || this.loading) {
        return;
      }

      if (this.ctaKind === 'connect') {
        if (!this.connectSlackUrl) {
          this.$appMessage('Unable to open Slack setup. Please try again.');

          return;
        }

        window.open(this.connectSlackUrlWithSurface(), '_blank', 'noopener,noreferrer');

        return;
      }

      this.enableNotifications();
    },
    async enableNotifications() {
      this.loading = true;

      try {
        const response = await SmartNotificationsSettingsResource.create(
          { organizationId: this.organizationId },
          { ...slackUaNotificationParams(), nudge_surface: this.source },
          { abortReload: true },
        );

        if (response?.errors) {
          this.$appMessage('Unable to enable Slack notifications. Please try again.');

          return;
        }

        this.$appMessage('Unusual activity Slack notifications are on.');
        this.$emit('completed');
      } catch (error) {
        if (![401, 403].includes(error?.status)) {
          this.$appMessage('Unable to enable Slack notifications. Please try again.');
        }
      } finally {
        this.loading = false;
      }
    },
  },
};
</script>
